import { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import API_BASE_URL from "../config";

/* 🔔 Toast */
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

/* ⚠️ Sweet Alert */
import Swal from "sweetalert2";

/* 🎯 Icons */
import {
  FaGavel,
  FaClipboardList,
  FaCheckCircle,
  FaTimesCircle,
  FaClock,
  FaWindowClose,
  FaFileAlt,
  FaCalendarAlt,
  FaUser,
  FaMapMarkerAlt,
  FaComments,
  FaEdit,
  FaTrash,
  FaDownload,
  FaChartBar,
  FaExclamationTriangle
} from "react-icons/fa";
import {
  MdOutlinePendingActions,
  MdOutlineCheckCircleOutline,
  MdOutlineEventAvailable
} from "react-icons/md";
import {
  FiDownload,
  FiRefreshCw
} from "react-icons/fi";

import "../styles/SolicitorCases.css";

/* ======================
   CASE STATUS LABELS
====================== */
const CASE_STATUS_LABELS = {
  PENDING: "Pending",
  FILED_IN_COURT: "Filed in Court",
  HEARING: "In Hearing",
  ADJOURNED: "Adjourned",
  ALLOWED: "Allowed",
  DISMISSED: "Dismissed",
  DISPOSED: "Disposed",
  CLOSED: "Closed",
  WITHDRAWN: "Withdrawn"
};

export default function SolicitorCases() {
  const { token } = useAuth();

  const [cases, setCases] = useState([]);
  const [expandedId, setExpandedId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exportLoading, setExportLoading] = useState(false);

  // 🔥 TAB STATE
  const [activeTab, setActiveTab] = useState("pending");

  // 🔥 STATS STATE
  const [stats, setStats] = useState({
    pending: 0,
    hearing: 0,
    closed: 0,
    total: 0
  });

  /* 🔹 Add Hearing Form */
  const [hearingForm, setHearingForm] = useState({
    hearingDateTime: "",
    courtName: "",
    courtLocation: "",
    judgeName: "",
    remarks: "",
  });

  /* 🔹 Edit Hearing */
  const [editingHearingId, setEditingHearingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [planName, setPlanName] = useState(null);

  const fetchUserPlan = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const data = await res.json();

      setPlanName(data?.user?.subscription?.planName || "FREE");

    } catch (err) {
      console.error("Plan fetch error:", err);
    }
  };

  useEffect(() => {
    fetchCases();
    fetchUserPlan();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ======================
     FETCH CASES
  ====================== */
  const fetchCases = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_BASE_URL}/api/cases/solicitor`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      const list = data.cases || [];
      setCases(list);

      /* 🔥 CALCULATE STATS */
      const pendingCount = list.filter(c =>
        ["PENDING", "FILED_IN_COURT", "ADJOURNED"].includes(c.caseStatus)
      ).length;

      const hearingCount = list.filter(c =>
        c.caseStatus === "HEARING"
      ).length;

      const closedCount = list.filter(c =>
        ["ALLOWED", "DISMISSED", "DISPOSED", "CLOSED", "WITHDRAWN"].includes(c.caseStatus)
      ).length;

      setStats({
        pending: pendingCount,
        hearing: hearingCount,
        closed: closedCount,
        total: list.length
      });
    } catch (error) {
      console.error("Fetch cases error:", error);
      toast.error("Failed to load cases");
    } finally {
      setLoading(false);
    }
  };

  const toggleDetails = (id) => {
    setExpandedId(expandedId === id ? null : id);
    setEditingHearingId(null);
  };

  /* ======================
     🔥 FILTER BASED ON TAB
  ====================== */
  const pendingCases = cases.filter(c =>
    ["PENDING", "FILED_IN_COURT", "HEARING", "ADJOURNED"].includes(c.caseStatus)
  );

  const completedCases = cases.filter(c =>
    ["ALLOWED", "DISMISSED", "DISPOSED", "CLOSED", "WITHDRAWN"].includes(c.caseStatus)
  );

  const visibleCases = activeTab === "pending" ? pendingCases : completedCases;

  /* ======================
     RENDER STATUS BADGE
  ====================== */
  const renderStatusBadge = (caseItem) => {
    let badgeConfig = {
      icon: <FaExclamationTriangle size={14} />,
      label: CASE_STATUS_LABELS[caseItem.caseStatus] || "Unknown",
      color: "#6b7280",
      bgColor: "#f3f4f6"
    };

    switch (caseItem.caseStatus) {
      case "PENDING":
      case "FILED_IN_COURT":
      case "ADJOURNED":
        badgeConfig = {
          icon: <MdOutlinePendingActions size={14} />,
          label: CASE_STATUS_LABELS[caseItem.caseStatus],
          color: "#f59e0b",
          bgColor: "#fef3c7"
        };
        break;
      case "HEARING":
        badgeConfig = {
          icon: <FaGavel size={14} />,
          label: "In Hearing",
          color: "#8b5cf6",
          bgColor: "#ede9fe"
        };
        break;
      case "ALLOWED":
        badgeConfig = {
          icon: <FaCheckCircle size={14} />,
          label: "Allowed",
          color: "#10b981",
          bgColor: "#d1fae5"
        };
        break;
      case "DISMISSED":
      case "CLOSED":
      case "WITHDRAWN":
        badgeConfig = {
          icon: <FaWindowClose size={14} />,
          label: CASE_STATUS_LABELS[caseItem.caseStatus],
          color: "#dc2626",
          bgColor: "#fee2e2"
        };
        break;
      case "DISPOSED":
        badgeConfig = {
          icon: <MdOutlineCheckCircleOutline size={14} />,
          label: "Disposed",
          color: "#3b82f6",
          bgColor: "#dbeafe"
        };
        break;
      default:
        break;
    }

    return (
      <span
        className="case-status-badge"
        style={{
          backgroundColor: badgeConfig.bgColor,
          color: badgeConfig.color
        }}
      >
        {badgeConfig.icon}
        <span>{badgeConfig.label}</span>
      </span>
    );
  };

  /* ======================
     RENDER STATS CARDS
  ====================== */
  const renderStatsCards = () => (
    <div className="case-stats-grid">
      <div className="case-stat-card case-stat-pending">
        <div className="case-stat-icon">
          <MdOutlinePendingActions size={24} />
        </div>
        <div className="case-stat-content">
          <h3 className="case-stat-value">{stats.pending}</h3>
          <p className="case-stat-label">Pending</p>
          <span className="case-stat-subtext">Active Cases</span>
        </div>
      </div>

      <div className="case-stat-card case-stat-hearing">
        <div className="case-stat-icon">
          <FaGavel size={24} />
        </div>
        <div className="case-stat-content">
          <h3 className="case-stat-value">{stats.hearing}</h3>
          <p className="case-stat-label">In Hearing</p>
          <span className="case-stat-subtext">Court Proceedings</span>
        </div>
      </div>

      <div className="case-stat-card case-stat-closed">
        <div className="case-stat-icon">
          <FaWindowClose size={24} />
        </div>
        <div className="case-stat-content">
          <h3 className="case-stat-value">{stats.closed}</h3>
          <p className="case-stat-label">Closed</p>
          <span className="case-stat-subtext">Resolved Cases</span>
        </div>
      </div>

      <div className="case-stat-card case-stat-total">
        <div className="case-stat-icon">
          <FaChartBar size={24} />
        </div>
        <div className="case-stat-content">
          <h3 className="case-stat-value">{stats.total}</h3>
          <p className="case-stat-label">Total Cases</p>
          <span className="case-stat-subtext">All Time</span>
        </div>
      </div>
    </div>
  );

  /* ======================
     ADD HEARING
  ====================== */
  // eslint-disable-next-line no-unused-vars
  const addHearing = async (caseId) => {
    if (!hearingForm.hearingDateTime || !hearingForm.courtName) {
      toast.error("Hearing date & court name are required");
      return;
    }

    try {
      await fetch(`${API_BASE_URL}/api/cases/${caseId}/hearings`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(hearingForm),
      });

      setHearingForm({
        hearingDateTime: "",
        courtName: "",
        courtLocation: "",
        judgeName: "",
        remarks: "",
      });

      toast.success("Hearing added successfully");
      fetchCases();
    } catch (error) {
      toast.error("Failed to add hearing");
    }
  };

  /* ======================
     UPDATE CASE STATUS
  ====================== */
  const updateCaseStatus = async (caseId, status) => {
    const confirm = await Swal.fire({
      title: "Update Case Status?",
      text: `Change status to "${CASE_STATUS_LABELS[status]}"?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, Update",
      confirmButtonColor: "#3b82f6",
      cancelButtonColor: "#6b7280"
    });

    if (!confirm.isConfirmed) return;

    try {
      await fetch(`${API_BASE_URL}/api/cases/${caseId}/status`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ caseStatus: status }),
      });

      toast.success("Case status updated");
      fetchCases();
    } catch (error) {
      toast.error("Failed to update case status");
    }
  };

  /* ======================
     START EDIT
  ====================== */
  const startEdit = (hearing) => {
    setEditingHearingId(hearing._id);
    setEditForm({
      hearingDateTime: hearing.hearingDateTime?.slice(0, 16),
      courtName: hearing.courtDetails?.courtName || "",
      courtLocation: hearing.courtDetails?.courtLocation || "",
      judgeName: hearing.courtDetails?.judgeName || "",
      remarks: hearing.courtDetails?.remarks || "",
    });
  };

  /* ======================
     SAVE EDIT
  ====================== */
  const saveEdit = async (caseId, hearingId) => {
    try {
      await fetch(`${API_BASE_URL}/api/cases/${caseId}/hearings/${hearingId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          hearingDateTime: editForm.hearingDateTime,
          courtDetails: {
            courtName: editForm.courtName,
            courtLocation: editForm.courtLocation,
            judgeName: editForm.judgeName,
            remarks: editForm.remarks,
          },
        }),
      });

      setEditingHearingId(null);
      toast.success("Hearing updated successfully");
      fetchCases();
    } catch (error) {
      toast.error("Failed to update hearing");
    }
  };

  /* ======================
     DELETE HEARING
  ====================== */
  const deleteHearing = async (caseId, hearingId) => {
    const confirm = await Swal.fire({
      title: "Delete Hearing?",
      text: "This action cannot be undone",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, Delete",
      confirmButtonColor: "#dc2626",
      cancelButtonColor: "#6b7280"
    });

    if (!confirm.isConfirmed) return;

    try {
      await fetch(
        `${API_BASE_URL}/api/cases/${caseId}/hearings/${hearingId}`,
        {
          method: "DELETE",
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      toast.success("Hearing deleted successfully");
      fetchCases();
    } catch (error) {
      toast.error("Failed to delete hearing");
    }
  };

  /* ======================
     EXPORT CASES
  ====================== */
  const handleExport = async () => {
    if (planName === "FREE") {
    toast.error("Upgrade plan to export Excel");
    return;
  }
    if (visibleCases.length === 0) {
      toast.warning("No cases to export");
      return;
    }

    try {
      setExportLoading(true);
      const res = await fetch(
        `${API_BASE_URL}/api/cases/solicitor/export/excel?status=${activeTab}`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (!res.ok) throw new Error("Export failed");

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download = activeTab === "pending" ? "Pending_Cases.xlsx" : "Completed_Cases.xlsx";

      document.body.appendChild(link);
      link.click();
      link.remove();

      toast.success("Excel exported successfully");

    } catch (error) {
      console.error("Export error:", error);
      toast.error("Failed to export Excel");
    } finally {
      setExportLoading(false);
    }
  };

  return (
    <div className="solicitor-cases-wrapper">
      {/* 🔔 Toast Container */}
      <ToastContainer position="top-right" autoClose={2500} />

      {/* 🔹 HEADER */}
      <div className="solicitor-cases-header">
        <div className="solicitor-header-left">
          <h1 className="case-page-title">Court Cases Management</h1>
          <p className="case-page-subtitle">
            Track and manage your court cases and hearings
          </p>
        </div>
        <div className="solicitor-header-right">
          <button
            className="case-refresh-btn"
            onClick={fetchCases}
            disabled={loading}
          >
            <FiRefreshCw size={16} />
            <span>{loading ? "Refreshing..." : "Refresh"}</span>
          </button>
          <button
            className="case-export-btn"
            onClick={handleExport}
            disabled={exportLoading || visibleCases.length === 0 || planName === "FREE" }
            title={visibleCases.length === 0 ? "No cases to export" : planName === "FREE" ? "Upgrade plan to export Excel" : "Export to Excel"}
          >
            {exportLoading ? (
              <>
                <div className="case-export-btn-spinner"></div>
                <span>Exporting...</span>
              </>
            ) : (
              <>
                <FiDownload size={18} />
                <span style={{ color: "white" }}>
                  Export ({visibleCases.length})
                </span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* 🔥 STATS */}
      {renderStatsCards()}

      {/* 🔥 MAIN CONTENT */}
      <div className="solicitor-cases-content">
        {/* Tabs */}
        <div className="case-tabs-container">
          <div className="case-main-tabs">
            <button
              className={`case-tab-btn ${activeTab === "pending" ? "case-tab-active" : ""}`}
              onClick={() => {
                setActiveTab("pending");
                setExpandedId(null);
              }}
            >
              <MdOutlinePendingActions size={16} />
              <span>Pending Cases ({pendingCases.length})</span>
            </button>

            <button
              className={`case-tab-btn ${activeTab === "completed" ? "case-tab-active" : ""}`}
              onClick={() => {
                setActiveTab("completed");
                setExpandedId(null);
              }}
            >
              <MdOutlineCheckCircleOutline size={16} />
              <span>Completed Cases ({completedCases.length})</span>
            </button>
          </div>
        </div>

        {/* 🔥 CASES LIST */}
        <div className="case-list-container">
          {loading ? (
            <div className="case-loading-state">
              <div className="case-loading-spinner"></div>
              <p>Loading court cases...</p>
            </div>
          ) : visibleCases.length === 0 ? (
            <div className="case-empty-state">
              <MdOutlineEventAvailable size={48} />
              <p>No {activeTab === "pending" ? "pending" : "completed"} cases available</p>
              <small>Try selecting a different tab or check back later</small>
            </div>
          ) : (
            <div className="case-cards-grid">
              {visibleCases.map((c) => (
                <div key={c._id} className="case-card">
                  {/* Card Header */}
                  <div className="case-card-header">
                    <div className="case-client-info">
                      <div className="case-client-avatar">
                        {c.userId?.userProfile?.photo ? (
                          <img
                            src={c.userId.userProfile.photo}
                            alt={c.userDetails?.name}
                            className="client-avatar-img"
                          />
                        ) : (
                          <div className="client-avatar-fallback">
                            {c.userDetails?.name?.charAt(0)?.toUpperCase()}
                          </div>
                        )}
                      </div>


                      <div className="case-client-details">
                        <div className="case-client-name">
                          {c.userId?.name || c.userDetails?.name || "Client Name Not Available"}
                        </div>
                        <div className="case-client-meta">
                          <FaUser size={12} />
                          <span>{c.userId?.email || c.userDetails?.email || "No email provided"}</span>
                        </div>
                      </div>
                    </div>
                    <div className="case-status-container">
                      {renderStatusBadge(c)}
                    </div>
                  </div>

                  {/* Card Body */}
                  <div className="case-card-body">
                    <div className="case-id-section">
                      <FaClipboardList size={14} />
                      <strong>Case ID:</strong>
                      <span className="case-id-value">{c.caseId || "N/A"}</span>
                    </div>

                    <div className="case-meta-info">
                      <div className="case-date-info">
                        <FaCalendarAlt size={14} />
                        <span>
                          Created: {new Date(c.createdAt).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric'
                          })}
                        </span>
                      </div>
                      {c.lastHearingDate && (
                        <div className="case-hearing-info">
                          <FaClock size={14} />
                          <span>
                            Last Hearing: {new Date(c.lastHearingDate).toLocaleDateString()}
                          </span>
                        </div>
                      )}
                    </div>

                    <div className="case-actions">
                      <button
                        className="case-view-details-btn"
                        onClick={() => toggleDetails(c._id)}
                      >
                        {expandedId === c._id ? "Hide Details" : "View Details"}
                      </button>

                      <div className="case-status-selector">
                        <select
                          className="case-status-dropdown"
                          value={c.caseStatus}
                          onChange={(e) => updateCaseStatus(c._id, e.target.value)}
                        >
                          {Object.keys(CASE_STATUS_LABELS).map((s) => (
                            <option key={s} value={s}>
                              {CASE_STATUS_LABELS[s]}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Expanded Details */}
                  {expandedId === c._id && (
                    <div className="case-expanded-details">
                      {/* USER DOCUMENTS - Updated to show 2 per row */}
                      <div className="case-details-section">
                        <h4>
                          <FaFileAlt size={16} />
                          <span>Case Documents</span>
                        </h4>
                        {!c.documents || c.documents.length === 0 ? (
                          <div className="case-no-documents">
                            <p>No documents uploaded by client</p>
                          </div>
                        ) : (
                          <div className="case-documents-grid">
                            {c.documents.map((doc) => (
                              <div key={doc._id} className="case-document-card">
                                <div className="document-header">
                                  <FaFileAlt size={18} />
                                  <div className="document-info">
                                    <strong>{doc.documentType}</strong>
                                    <small>{doc.originalName}</small>
                                  </div>
                                </div>
                                <a
                                  href={doc.blobUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="document-download-btn"
                                >
                                  <FaDownload size={14} />
                                  <span>Download</span>
                                </a>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* HEARING HISTORY */}
                      <div className="case-details-section">
                        <h4>
                          <FaGavel size={16} />
                          <span>Hearing History</span>
                        </h4>

                        {!c.hearings?.length ? (
                          <div className="case-no-hearings">
                            <p>No hearings scheduled yet</p>
                          </div>
                        ) : (
                          <div className="hearings-timeline">
                            {c.hearings.map((h) => (
                              <div key={h._id} className="hearing-timeline-item">
                                <div className="timeline-marker"></div>
                                <div className="hearing-content">
                                  {editingHearingId === h._id ? (
                                    <div className="hearing-edit-form">
                                      <div className="form-row">
                                        <input
                                          type="datetime-local"
                                          className="case-form-input"
                                          value={editForm.hearingDateTime}
                                          onChange={(e) =>
                                            setEditForm({
                                              ...editForm,
                                              hearingDateTime: e.target.value,
                                            })
                                          }
                                        />
                                      </div>
                                      <div className="form-row">
                                        <input
                                          className="case-form-input"
                                          placeholder="Court Name"
                                          value={editForm.courtName}
                                          onChange={(e) =>
                                            setEditForm({
                                              ...editForm,
                                              courtName: e.target.value,
                                            })
                                          }
                                        />
                                      </div>
                                      <div className="form-actions">
                                        <button
                                          className="case-save-btn"
                                          onClick={() => saveEdit(c._id, h._id)}
                                        >
                                          <FaCheckCircle size={14} />
                                          <span>Save</span>
                                        </button>
                                        <button
                                          className="case-cancel-btn"
                                          onClick={() => setEditingHearingId(null)}
                                        >
                                          <FaTimesCircle size={14} />
                                          <span>Cancel</span>
                                        </button>
                                      </div>
                                    </div>
                                  ) : (
                                    <>
                                      <div className="hearing-header">
                                        <div className="hearing-date">
                                          <FaCalendarAlt size={14} />
                                          <span>
                                            {new Date(h.hearingDateTime).toLocaleString()}
                                          </span>
                                        </div>
                                        <div className="hearing-actions">
                                          <button
                                            className="case-edit-btn"
                                            onClick={() => startEdit(h)}
                                          >
                                            <FaEdit size={14} />
                                            <span>Edit</span>
                                          </button>
                                          <button
                                            className="case-delete-btn"
                                            onClick={() => deleteHearing(c._id, h._id)}
                                          >
                                            <FaTrash size={14} />
                                            <span>Delete</span>
                                          </button>
                                        </div>
                                      </div>
                                      <div className="hearing-details">
                                        <div className="hearing-detail">
                                          <FaMapMarkerAlt size={14} />
                                          <span><strong>Court:</strong> {h.courtDetails?.courtName}</span>
                                        </div>
                                        {h.courtDetails?.remarks && (
                                          <div className="hearing-detail">
                                            <FaComments size={14} />
                                            <span><strong>Remarks:</strong> {h.courtDetails.remarks}</span>
                                          </div>
                                        )}
                                      </div>
                                    </>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}